import { ref, computed } from 'vue';
import { log, error as logError } from '../utils/DebugLogger';
import { useSOCheckout } from './useSOCheckout';

const DEBOUNCE_MS = 2000;

export type QrcodeUniquenessStatus = 'idle' | 'checking' | 'unique' | 'duplicate' | 'error';

/**
 * Live single-QR uniqueness check for the OS-item wizard.
 *
 * Owns a 2s debounce + a stale-response guard so fast typing/scanning collapses
 * to a single settled request. Fail-closed: only status === 'unique' gates Next
 * open; idle/checking/duplicate/error all keep it disabled.
 *
 * check() flips to 'checking' SYNCHRONOUSLY (so Next disables and the loading
 * message shows the instant the user types/scans, before the debounce elapses),
 * then resolves to 'unique'/'duplicate' on response or 'error' on failure.
 */
export function useQrcodeUniquenessCheck() {
  const { checkQrcodeUnique } = useSOCheckout();

  const status = ref<QrcodeUniquenessStatus>('idle');
  const isUnique = computed<boolean>(() => status.value === 'unique');

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let latestRequestId = 0;

  const clearTimer = (): void => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };

  const check = (
    qrcode: string,
    excludeItemId?: number | string | null,
    orderId?: number | string | null,
  ): void => {
    clearTimer();
    // Empty/whitespace QR — reset, no call. Do NOT trim the checked value itself
    // (below) so live-check and save-time isQrcodeUnique agree on the same string.
    if (!qrcode || qrcode.trim() === '') {
      latestRequestId += 1;
      status.value = 'idle';
      return;
    }

    const requestId = ++latestRequestId;
    // Synchronous: gate Next + show the loading message immediately.
    status.value = 'checking';

    debounceTimer = setTimeout(() => {
      log('useQrcodeUniquenessCheck', 'check', 'Checking QR', { qrcode, excludeItemId, orderId });
      checkQrcodeUnique(qrcode, excludeItemId, orderId)
        .then((unique) => {
          if (requestId !== latestRequestId) return;
          status.value = unique ? 'unique' : 'duplicate';
        })
        .catch((err) => {
          if (requestId !== latestRequestId) return;
          logError('useQrcodeUniquenessCheck', 'check', 'Error', err);
          status.value = 'error';
        });
    }, DEBOUNCE_MS);
  };

  const reset = (): void => {
    clearTimer();
    latestRequestId += 1;
    status.value = 'idle';
  };

  return {
    status,
    isUnique,
    check,
    reset,
  };
}
